import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ViewContainerRef,
  ViewEncapsulation,
} from '@angular/core';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { GUID } from '@barthauer/abc/core';
import { CoreDataEntity, PersistMode } from '@barthauer/abc/core/data';
import { ResourceManager } from '@barthauer/abc/core/resources';
import {
  DialogResult,
  MessageBoxButtons,
  MessageBoxIcon,
  MessageBoxService,
  MessageMode,
  MessageSettings,
} from '@barthauer/abc/messages';
import {
  AbcContainerService,
  AbcEditorComponent,
  AbcWindowComponent,
  IWindow,
  WindowStyle,
} from '@barthauer/abc/ui-components';
import { LoginInfo, UserService } from '@barthauer/basys/core/authentication';
import { DevExWindowComponent } from 'libs/abc/ui-components/src/lib/devExtreme/src/devExWindow/devExWindow.component';
import { Observable, of, Subject } from 'rxjs';
import { map, switchMap, takeUntil } from 'rxjs/operators';
import { CommentModel } from '../../models/comment.model';
import { CommentsService } from '../../services/comments.service';

@Component({
    selector: 'abc-common-editors-comments-list',
    templateUrl: './list.component.html',
    styleUrls: ['./list.component.scss'],
    encapsulation: ViewEncapsulation.Emulated,
    standalone: false
})
export class AbcCommentsListComponent implements OnInit, OnDestroy {
  public res = ResourceManager.getResources('abc-common-editors-comments');

  public width: string;
  @ViewChild('abcEditorContainer', { static: false, read: ViewContainerRef }) abcEditor: AbcEditorComponent;

  private parentId: string;
  private parentObjectType: string;
  @Input() set parentInfo(data: CoreDataEntity) {
    this.parentId = data?.id;
    this.parentObjectType = data?.objectType?.toString();
    if (this.autoQueryComments) {
      this.initializeComments();
    }
  }

  private _identifier: string;
  @Input() set identifier(identifier: string) {
    this._identifier = identifier;
  }

  get identifier() {
    if (!this._identifier) {
      return 'Standard';
    }

    return this._identifier;
  }

  @Input() commentsListData: any; //Needs further research
  @Input() oPathFilter: string;
  @Input() isReadOnly: boolean;
  @Output() commentsCount: EventEmitter<object> = new EventEmitter();

  private autoQueryComments: boolean = true;
  private _comments: CommentModel[];
  @Input()
  public get comments(): CommentModel[] {
    return this._comments ?? [];
  }
  public set comments(value: CommentModel[]) {
    if (value) {
      this.autoQueryComments = false;
    }

    this._comments = value;
  }
  public isCommentExpanded: Map<CommentModel, boolean> = new Map<CommentModel, boolean>();

  private userInfo: LoginInfo;

  public settings: MessageSettings;
  public showLoader: boolean;
  public deleteCommentSubject = new Subject<CommentModel>();
  private unsubscribe$ = new Subject<void>();

  constructor(
    private messageBoxService: MessageBoxService,
    private abcContainer: AbcContainerService,
    public iconRegistry: MatIconRegistry,
    public sanitizer: DomSanitizer,
    private commentService: CommentsService,
    private userService: UserService
  ) {
    this.registerIcons();
  }

  private registerIcons(): void {
    this.iconRegistry.addSvgIcon('edit_com', this.sanitizer.bypassSecurityTrustResourceUrl('assets/images/edit.svg'));
    this.iconRegistry.addSvgIcon(
      'del_com',
      this.sanitizer.bypassSecurityTrustResourceUrl('assets/images/DeleteComments.svg')
    );
  }

  public ngOnInit(): void {
    window.addEventListener('resize', this.setPopupDimensions.bind(this));
    this.setPopupDimensions();
    this.settings = new MessageSettings();
    this.settings.mode = MessageMode.Question;
    this.settings.icon = MessageBoxIcon.Question;

    this.userService
      .getCurrentLoginInfo()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((userInfo) => {
        this.userInfo = userInfo;
      });

    this.deleteCommentSubject
      .asObservable()
      .pipe(
        switchMap((comment) => {
          return this.messageBoxService.showMessageBox(this.settings).pipe(
            map((result) => {
              if (DialogResult.Yes === result['result']) {
                this.commentService.deleteComment(comment);
                const index = this.comments.findIndex((commentData) => commentData.Id === comment.Id);
                if (index >= 0) {
                  this._comments.splice(index, 1);
                }
                this.commentsCount.emit({ isIncremented: false, elementName: 'Comments' });
              }

              this.messageBoxService.dialog.closeAll();
            })
          );
        })
      )
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe();
  }
  public setPopupDimensions(): void {
    this.width = window.innerWidth < 670 ? '85%' : '';
  }
  private async initializeComments(): Promise<void> {
    this.showLoader = true;
    if (this.parentId) {
      const data: CommentModel[] = await this.commentService.queryComments(
        this.parentId,
        this.parentObjectType,
        this.oPathFilter
      );

      if (data.length > 0) {
        this._comments = data;
      } else {
        this._comments = [];
      }
    } else {
      this._comments = [];
    }

    const arr = this.oPathFilter?.split("'");
    if (arr?.length === 3) {
      this.identifier = arr[1];
    }

    this.showLoader = false;
  }

  public expandCollapseComment(comment: CommentModel): void {
    this.isCommentExpanded.set(comment, !this.isCommentExpanded.get(comment));
  }

  public getFirstLettersFromName(name: string): string {
    let firstLetters: string = '';

    if (name) {
      if (name.indexOf('.') >= 0) {
        firstLetters = this.joinFirstLettersBySeperator(name, '.');
      } else if (name.indexOf(' ') >= 0) {
        firstLetters = this.joinFirstLettersBySeperator(name, ' ');
      } else {
        firstLetters = name.charAt(0).toUpperCase() + name.charAt(1).toLowerCase();
      }
    }

    return firstLetters;
  }

  private joinFirstLettersBySeperator(name: string, seperator: string) {
    return name
      .split(seperator)
      .map((n) => n[0])
      .join('.');
  }

  public isCommentUpdated(insertDate: string, updateDate: string, insertTime: string, updateTime: string): boolean {
    return insertDate !== updateDate || (insertDate === updateDate && insertTime !== updateTime);
  }

  public addComment() {
    this.addEditCommentCore();
  }

  public editComment(comment: CommentModel) {
    this.addEditCommentCore(comment);
  }

  private async addEditCommentCore(comment?: CommentModel): Promise<void> {
    let componentRef: AbcEditorComponent;
    await this.openAbcWindow()
      .pipe(
        switchMap((activeComponent: DevExWindowComponent) => {
          const componentInstance = activeComponent.openWindow();
          componentRef = <AbcEditorComponent>componentInstance.instance;
          componentRef.setIdentifier(comment ? comment.Identifier : this.identifier);

          return activeComponent.onAfterOpened.pipe(
            switchMap(() => {
              if (comment) {
                componentRef.setText(comment.Comment);
              }

              return activeComponent.onCloseClick.pipe(
                switchMap(() => {
                  const plainText = this.getSanitizedPlainText(componentRef);

                  if (comment ? plainText !== comment.Text : plainText !== '') {
                    this.settings.button = MessageBoxButtons.YesNoCancel;
                    this.settings.message = this.res('CommentsList-ConfirmExitMessage');

                    return this.messageBoxService.showMessageBox(this.settings).pipe(
                      map(async (result: DialogResult) => {
                        if (DialogResult.Yes === result['result']) {
                          if (comment) {
                            this.updateCommentModel(comment, componentRef);
                            this.saveComment(comment, PersistMode.Update);
                          } else {
                            comment = await this.createNewCommentModel(componentRef);
                            this.saveComment(comment, PersistMode.Insert);
                          }

                          this.messageBoxService.dialog.closeAll();
                          activeComponent.closeDialog();
                        } else if (DialogResult.Cancel === result['result']) {
                          this.messageBoxService.dialog.closeAll();
                        } else {
                          this.messageBoxService.dialog.closeAll();
                          activeComponent.closeDialog();
                        }

                        return of();
                      })
                    );
                  } else {
                    activeComponent.closeDialog();
                    return of();
                  }
                })
              );
            })
          );
        })
      )
      .toPromise();
  }

  private openAbcWindow(): Observable<DevExWindowComponent> {
    const style: WindowStyle = {
      minHeight: '350px',
      minWidth: '200px',
      maxHeight: '350px',
      maxWidth: '600px',
      width: this.width,
      shading: true,
      resizable: true,
    };
    const WindowAttr: IWindow = {
      id: 'Editor',
      label: this.res('CommentsList-Editor'),
      minimizeButton: false,
      maximizeButton: false,
      component: AbcEditorComponent,
      setWindowStyle: (windowStyle: WindowStyle) => (windowStyle = style),
    };
    const window = this.abcContainer.createComponent(this.abcEditor, AbcWindowComponent, WindowAttr);
    const windowInstance = <AbcWindowComponent>window.componentRef.instance;
    windowInstance.allowDirectClose = false;

    return windowInstance.createComponentInstance();
  }

  private async createNewCommentModel(componentRef: AbcEditorComponent): Promise<CommentModel> {
    const date = new Date();
    let seqNr: number;
    if (this.autoQueryComments) {
      seqNr = await this.commentService.getNextSeqNumber(this.parentObjectType, this.parentId, this.identifier);
    } else if (this.comments.length) {
      seqNr = Math.max(...this.comments?.map((_) => _.SeqNr ?? 0)) + 1;
    } else {
      seqNr = 1;
    }

    const comment: CommentModel = new CommentModel({
      SeqNr: seqNr,
      Id: GUID.newGUID(),
      ParentId: this.parentId,
      Identifier: this.identifier,
      Text: this.getSanitizedPlainText(componentRef),
      Comment: componentRef.getText(),
      SysUserNameInsert: this.userInfo?.userName,
      SysUserNameUpdate: this.userInfo?.userName,
      SysDateInsert: date,
      SysDateUpdate: date,
    });

    return comment;
  }

  private updateCommentModel(comment: CommentModel, componentRef: AbcEditorComponent) {
    const date: Date = new Date();

    comment.Text = this.getSanitizedPlainText(componentRef);
    comment.Comment = componentRef.getText();
    comment.SysDateUpdate = date;
    comment.SysTimeUpdate = date.toISOString();
    comment.SysUserNameUpdate = this.userInfo.userName;
  }

  private async saveComment(comment: CommentModel, persistMode: PersistMode) {
    this.commentService.saveComment(comment, this.parentObjectType, persistMode);

    if (persistMode === PersistMode.Insert) {
      this.comments.unshift(comment);
      this.commentsCount.emit({ isIncremented: true, elementName: 'Comments' });
    }
  }

  public deleteComment(comment: CommentModel) {
    this.settings.button = MessageBoxButtons.YesNo;
    this.settings.message = this.res('CommentsList-ConfirmDeleteMessage');

    this.deleteCommentSubject.next(comment);
  }

  private getSanitizedPlainText(componentRef: AbcEditorComponent): string {
    let plainText = componentRef.getPlainText();
    plainText = plainText.trim();

    return plainText;
  }

  public ngOnDestroy(): void {
    this.unsubscribe$.next(null);
    this.unsubscribe$.complete();
    window.removeEventListener('resize', this.setPopupDimensions.bind(this));
  }
}
